import {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Credentials } from 'crono-fe-common/types/credentials';
import client from 'utils/clients/client';
import { ENDPOINTS } from 'config/endpoints';
import { Request } from 'crono-fe-common/types/request';
import { getFullName, User } from 'crono-fe-common/types/user';
import { useAsync } from 'hooks/useAsync';
import { SALESFORCE_DOMAIN_KEY } from 'config/localStorage';
import PATH, {
  noRedirectToLoginPathnames,
} from 'crono-fe-common/constants/path';
import { Response } from 'crono-fe-common/types/response';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FeConstants } from 'constants/FeConstants';
import { useAnalytics } from 'context/analytics';
import { isLocal } from 'utils/uiHelpers';
import {
  isOnboardingToDo,
  IOnboardingStepsWithSelectName,
} from 'utils/businessLogic';
import useGetUserOnboardingIntegrations from 'hooks/services/user/useGetUserOnboardingIntegrations';
import isOauthCallbackPathname from 'pages/connect/oauthCallbackPathname';
import { useConditionalSnackBar } from 'context/snackbar';
import { fullName, getError } from 'crono-fe-common/utils';
import { useLinkedinHasSalesNav } from 'crono-fe-common/hooks/crono-extension/gateway';
import { Constants } from 'crono-fe-common/constants/constants';
import usePatchUserPreferences from 'hooks/services/user/usePatchUserPreferences';
import { cookieDomain, cookies } from 'constants/crono-cookie';
import { useUpgradeModal } from 'context/upgradeModal';
import useGetSubscription from 'hooks/services/subscription/useGetSubscription';
import { refreshCsrfToken } from 'utils/clients/csrf';
import { useRefreshCsrfOnTabActive } from 'hooks/useRefreshCsrfOnTabActive';
import { getSubscriptionPlanLabel } from 'crono-fe-common/types/enums/subscriptionPlanType';
import useLogout from 'hooks/services/auth/useLogout';
import useLogin from 'hooks/services/auth/useLogin';
import Gleap from 'gleap';
import { getSupportTier } from 'utils/supportTier';
import { AuthContext } from './contexts';

const salesNavDetectionMaxAttempts = 3;
const salesNavDetectionRetryDelayMs = 10000;

const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    data: user,
    error,
    isLoading: isAsyncLoading,
    isIdle,
    isError,
    setData,
    runAsync,
    reset,
  } = useAsync<User>();

  const isLoading = isIdle || isAsyncLoading;

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const mfaRequired = useRef(false);
  const [mfaTempToken, setMfaTempToken] = useState<string | null>(null);
  const [mfaConfigured, setMfaConfigured] = useState<boolean | null>(null);

  async function setUserTokens(user: User) {
    if (analytics) {
      //Identify the user only if it is not candidate, since candidates have a fake ID. The identification process will be done by after the candidate is converted to user
      if (!user.candidate) {
        analytics.identify(user.id.toString(), {
          email: user.email,
          name: getFullName(user),
        });

        if (user.company) {
          window.CustomerScoreTracker.identify({
            customer: {
              id: user.subscriptionId,
            },
            contact: {
              id: user.id,
              email: user.email,
              contact_name: getFullName(user),
            },
          });

          analytics.group(user.company.id.toString(), {
            id: user.company.id,
            userId: user.id,
            name: user.company.name,
            cronoPlan: getSubscriptionPlanLabel(user.subscriptionType),
          });
        }
      }
    }

    await refreshCsrfToken();
    if (user.salesforceSettings) {
      localStorage.setItem(
        SALESFORCE_DOMAIN_KEY,
        user.salesforceSettings.domain,
      );
    }
  }

  const isExpired = useMemo(() => {
    return (user && user.company?.expired) ?? false;
  }, [user]);

  //Every time I try to move I redirect to the expired if the user is expired
  useEffect(() => {
    if (isExpired) {
      navigate(PATH.EXPIRED);
    }
  }, [isExpired, location]);

  const checkIfLoggedOrCookiePresent = async () => {
    if (mfaRequired.current) return true;
    //If we are in the thank-you page I don't want to call the authMe, otherwise we would be rendered again back to the expired page, outside of the thank you
    const cookieToken = cookies.get(FeConstants.cronoThankYouCookie);
    const pathname = location.pathname;
    if (!(cookieToken && pathname === PATH.THANK_YOU)) {
      //In this case we also set, since it may be the first time
      const res = await refreshUser();
      if (!res) {
        logout(false);
        return false;
      }
      return true;
    }
    return true;
  };

  useEffect(() => {
    checkIfLoggedOrCookiePresent();
  }, [reset, cookies]);

  const { mutate: loginMutate, error: loginError } = useLogin();

  useConditionalSnackBar([
    {
      condition: !!loginError && loginError.response?.status !== 402,
      message: getError(loginError) ?? 'Something went wrong',
      severity: 'error',
    },
  ]);

  //To set the user given the User data (for example after a subscriptionComplete API call)
  const setNewUser = (newUser: User) => {
    setData(newUser);
    setUserTokens(newUser);
  };

  const login = async ({ email, password }: Credentials) => {
    await refreshCsrfToken();
    loginMutate(
      { email, password },
      {
        onSuccess: async (result) => {
          if (result.data) {
            const data = result.data;
            const user = data.data;

            if (data.mfaRequiredData) {
              mfaRequired.current = true;
              setMfaTempToken(data.mfaRequiredData.tempToken);
              setMfaConfigured(data.mfaRequiredData.mfaConfigured);
              setData(null);
              const targetPath = data.mfaRequiredData.mfaConfigured
                ? PATH.MFA
                : PATH.MFA_SETUP;
              navigate(targetPath);
              return;
            }
            await setUserTokens(user);
            setData(user);
          }
        },
        onError: (error) => {
          //In this case it is an expired subscription
          const axiosError = error as {
            response?: { status?: number; data?: { data?: User } };
          };
          if (axiosError.response?.status === 402) {
            const user = axiosError.response.data?.data as User;
            setUserTokens(user);
            setData(user);
          }
        },
      },
    );
  };

  const refreshUserNotSet = () => {
    if (mfaRequired.current) return;
    const request: Request = {
      url: ENDPOINTS.auth.me,
      config: {
        method: 'post',
      },
    };

    client(request)
      .then((result: Response<User>) => {
        if (result.data) {
          const userResult = result.data.data;
          setUserTokens(userResult);
          if (!user && userResult) {
            setData(userResult);
          }
        }
      })
      .catch((error) => {
        //In this case it is an expired subscription
        if (error.response?.status === 402) {
          const user = error.response.data.data;
          setUserTokens(user);
          setNewUser(user);
        }
      });
  };
  const refreshUser = async () => {
    if (mfaRequired.current) return true;
    try {
      await refreshCsrfToken();
    } catch (error) {
      console.error(error);
    }
    const request: Request = {
      url: ENDPOINTS.auth.me,
      config: {
        method: 'post',
      },
    };
    try {
      const promise = runAsync(
        client(request)
          .then((result: Response<User>) => {
            if (result.data) {
              const user = result.data.data;
              setOnboardingCallEnabled(true);
              setUserTokens(user);
              return user;
            }
          })
          .catch((error) => {
            //In this case it is an expired subscription
            if (error.response?.status === 402) {
              const user = error.response.data.data;
              setUserTokens(user);
              return user;
            }
          }),
      );
      await promise;
      return true;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    //I call the AUTH/ME to refresh the coookie every hour
    const interval = setInterval(refreshUserNotSet, 60 * 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [refreshUserNotSet]);

  useRefreshCsrfOnTabActive(!!user);

  const { mutateAsync: logoutCall } = useLogout();

  const logout = async (triggerLogoutApi = true) => {
    //The session teardown below must not depend on the API call: it would gate
    //the redirect on the round-trip, and a failing call would leave the user
    //logged in. It is awaited only at the end, so callers that reload the page
    //right after logging out don't cancel the request while it is in flight.
    const logoutRequest = triggerLogoutApi
      ? logoutCall().catch(() => undefined)
      : Promise.resolve();

    cookies.remove(FeConstants.cronoCsrfCookie, { domain: cookieDomain });
    cookies.remove(FeConstants.cronoCsrfLocalCookie, {
      domain: cookieDomain,
    });
    setData(null);
    queryClient.clear();
    window.CustomerScoreTracker.resetIdentity();
    const pathname = location.pathname;
    setTimeout(() => {
      const isNoRedirect = noRedirectToLoginPathnames.some(
        (link) => link.toLowerCase() === pathname.toLowerCase(),
      );
      if (isNoRedirect) return;
      navigate(PATH.LOGIN);
    }, 0);

    await logoutRequest;
  };

  useEffect(() => {
    //After logout the user is set to null, after this I can call the onboarding API
    if (user === null) {
      setOnboardingCallEnabled(true);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.candidate) {
      navigate(PATH.COMPLETE);
    }
  }, [user]);

  const enabledContextCalls = useMemo(() => {
    return !!user && !user.candidate && !user.company?.expired;
  }, [user]);

  //To prevent the call from being fired multiple times. When the onboardingState === null after checking with the user this is set to false
  const [onboardingCallEnabled, setOnboardingCallEnabled] =
    useState<boolean>(true);

  const {
    data: userOnboardingIntegrations,
    refetch: refetchOnboardingIntegration,
  } = useGetUserOnboardingIntegrations(
    enabledContextCalls && onboardingCallEnabled,
  );

  //This is needed to know if only 1 user license is available to show or not the step
  const { data: subscriptionData } = useGetSubscription(enabledContextCalls);

  useEffect(() => {
    if (!isLocal() && user) {
      const tier = getSupportTier(subscriptionData?.data.data.activeLicenses);
      Gleap.identify(user.id.toString(), {
        name: fullName(user.firstName, user.lastName),
        companyId: user.company?.id?.toString(),
        companyName: user.company?.name,
        email: user.email,
        plan: user.subscriptionType,
        customData: {
          subscriptionId: user.subscriptionId,
          ...(tier && { tier }),
        },
      });
    }
  }, [user, subscriptionData]);

  const onboardingRedirectDeferred = useRef(false);

  //Calculate the current state of the onBoarding:
  //- undefined: calculating;
  //- null: onBoarding done or user missing;
  //- else: onboarding to finish
  const onboardingState: IOnboardingStepsWithSelectName | null | undefined =
    useMemo(() => {
      //I make sure that both the API have been called
      if (
        user === undefined ||
        userOnboardingIntegrations === undefined ||
        subscriptionData === undefined
      )
        return undefined;
      if (!user) return null;
      const state = isOnboardingToDo(
        user,
        userOnboardingIntegrations?.data?.data ??
          user.integrationsOnboarding ??
          null,
        subscriptionData.data.data,
      );
      if (state !== null) {
        // An OAuth callback route is mid-exchange and leaves on its own once the
        // code settles. Pulling it out from under the handler loses the refreshed
        // user, so the step it lands on still reads the CRM as unconnected.
        if (isOauthCallbackPathname(location.pathname)) {
          onboardingRedirectDeferred.current = true;
        } else {
          onboardingRedirectDeferred.current = false;
          navigate(PATH.ONBOARDING);
        }
        return {
          ...state,
          hasToSelectName:
            (user.company?.subscriptionSetupCompleted &&
              user.firstName === '' &&
              user.lastName === '') ??
            false,
        };
      }
      setOnboardingCallEnabled(false);
      onboardingRedirectDeferred.current = false;
      return null;
    }, [user, userOnboardingIntegrations, subscriptionData]);

  // The memo above only re-runs when the session data changes, and an exchange
  // that never reached the API changes none of it — so a redirect it deferred
  // for a callback route has to be applied once that route has left.
  useEffect(() => {
    if (!onboardingRedirectDeferred.current) return;
    if (isOauthCallbackPathname(location.pathname)) return;
    onboardingRedirectDeferred.current = false;
    if (onboardingState) navigate(PATH.ONBOARDING);
  }, [location.pathname, onboardingState, navigate]);

  const [hasSalesNav, setHasSalesNav] = useState<boolean | null>(null);

  const [hasSalesNavCookie, setHasSalesNavCookie] = useState<boolean>(false);

  //If this is true the user is a salesNav and we can provide him with the salesNav funcionalities since we have the cookie
  const hasSalesNavWithCookie = useMemo(() => {
    return (hasSalesNav && hasSalesNavCookie) ?? false;
  }, [hasSalesNav, hasSalesNavCookie]);

  const [isSalesNavKnown, setIsSalesNavKnown] = useState<boolean>(false);

  const { call: getHasSalesNav } = useLinkedinHasSalesNav();
  useEffect(() => {
    if (!enabledContextCalls) return;

    let isCancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;

    const detectSalesNav = async (attempt: number): Promise<void> => {
      const response = await getHasSalesNav();
      if (isCancelled) return;

      // The gateway answers null for every failure — extension missing or out
      // of date, LinkedIn logged out, request timed out — which says nothing
      // about the user owning Sales Navigator, so it must never be persisted.
      if (response === null) {
        if (attempt < salesNavDetectionMaxAttempts) {
          retryTimeout = setTimeout(
            () => detectSalesNav(attempt + 1),
            salesNavDetectionRetryDelayMs,
          );
        } else {
          setHasSalesNav(false);
        }
        return;
      }

      setIsSalesNavKnown(true);
      setHasSalesNav(response);
    };

    void detectSalesNav(1);

    return () => {
      isCancelled = true;
      clearTimeout(retryTimeout);
      setIsSalesNavKnown(false);
    };
  }, [enabledContextCalls]);

  const automaticTaskLimitFromPref =
    user?.userPreferences?.automaticTaskLimit !== undefined &&
    user?.userPreferences?.automaticTaskLimit !== null
      ? user.userPreferences.automaticTaskLimit
      : null;
  const automaticLinkedinTaskExecutionLimit =
    automaticTaskLimitFromPref !== null
      ? automaticTaskLimitFromPref
      : Constants.maxAutomaticDailyDefault; //both sales nav and casual linkedin users have the same default limit

  const { mutate: patchUserPreferences } = usePatchUserPreferences();

  useEffect(() => {
    if (!user) return;
    if (!isSalesNavKnown) return;
    //We align the userPreferences with what we detect from linkedin
    if (hasSalesNav === false) {
      if (
        user.userPreferences?.hasSalesNav ||
        user.userPreferences?.hasSalesNav === null ||
        user.userPreferences?.hasSalesNav === undefined
      ) {
        patchUserPreferences({
          hasSalesNav: false,
        });
        if (user) {
          user.userPreferences.hasSalesNav = false;
        }
      }
    } else if (hasSalesNav === true) {
      if (
        user.userPreferences?.hasSalesNav === false ||
        user.userPreferences?.hasSalesNav === null ||
        user.userPreferences?.hasSalesNav === undefined
      ) {
        patchUserPreferences({
          hasSalesNav: true,
        });
        if (user) {
          user.userPreferences.hasSalesNav = true;
        }
      }
    }
  }, [hasSalesNav, isSalesNavKnown, user?.userPreferences]);

  const hasOpportunities = user?.otherSettings?.hasOpportunities ?? false;

  const { openModal, isUpgradeModalVisible } = useUpgradeModal();

  // [INFO]: For now we decided that we want to try hide things instead of showing the upgrade badges.
  const showUpgradeBadges = useMemo(() => {
    if (!user) return false;
    return false;
    // return showUpgradeBadgesUtil(user);
  }, [user]);

  useEffect(() => {
    Gleap.showFeedbackButton(!!user);
  }, [user]);

  const openPaywallIfNeeded = (onClose?: () => void) => {
    if (showUpgradeBadges && !isUpgradeModalVisible) {
      openModal({ onClose });
    }
  };

  // MFA route guard: keep user on the correct MFA page while MFA is pending
  useEffect(() => {
    if (!mfaRequired.current) return;
    const targetPath = mfaConfigured ? PATH.MFA : PATH.MFA_SETUP;
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  }, [location.pathname, mfaConfigured]);

  const cleanMfa = () => {
    mfaRequired.current = false;
    setMfaTempToken(null);
    setMfaConfigured(null);
    setData(null);
  };

  const value = {
    user,
    error,
    isLoading,
    isError,
    cookies,
    login,
    logout,
    refreshUser,
    onboardingState,
    refetchOnboardingIntegration,
    setNewUser,
    enabledContextCalls,
    isExpired,
    hasSalesNav,
    automaticLinkedinTaskExecutionLimit,
    setHasSalesNavCookie,
    hasSalesNavWithCookie,
    hasOpportunities,

    showUpgradeBadges,
    openPaywallIfNeeded,
    mfaConfigured,
    cleanMfa,
    mfaTempToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }

  return context;
}
